// @givmo/app-charity — the unified Grants & donations statement (CPA-WEB11). ★ THE numbers-are-the-law
// screen, rebuilt.
//
// It replaces three cards — Grants, Fundraisers, "Donors who chose to share" — that were always three
// PARTITIONS OF ONE SET of donations, presented as three unrelated features with two download buttons
// between them. Grant, fundraiser and donor are not three features; they are three ways to group the
// same donations. So this is one table with four views over one server read, and one workbook instead
// of two CSVs.
//
// It shipped for one cycle behind `VITE_CHARITY_STATEMENT_V2_ENABLED`, a build-time rollback lever
// (a variable flip + redeploy, instead of a revert through a promotion round on the program's most
// scrutinized screen). CPA-BE16 retired the knob with the page it selected against: this is the only
// page `/donations` renders.
//
// ★ MONEY LAW. Every figure is the server's, rendered verbatim. The headline is its own four-fact
// component (the retired page's five historic tiles went with it). All three headline
// money figures are NET, and Pending is server-authored — nothing here subtracts two amounts. Group
// subtotals likewise come from the statement's server-computed blocks, including the two remainder
// buckets that keep the groupings money-complete.
//
// ★ TILES AND TABLE FAIL INDEPENDENTLY. Two reads, two regions, two retries. One failing read never
// blanks the screen — a charity that can see its totals but not its rows can still answer the question
// it came for.
//
// NOT A RECEIPT: a transparency statement, never a tax/donation receipt (GCF's §319 CWA is the only
// receipt).
//
// The root keeps `data-testid="page-grants"` — the data contract e2e asserts, and the same id the old
// page carries, because it is the same screen.

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  charityAdminGetSection321DashboardOptions,
  charityAdminGetSection321StatementOptions,
} from '@givmo/api-client/query';
import { isGivmoError, type Section321StatementOut } from '@givmo/api-client';
import {
  Button,
  Card,
  cn,
  EmptyState,
  FilterChip,
  FOCUS_RING,
  Icon,
  SearchInput,
  Select,
  Skeleton,
  Tabs,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { isOwnerRole } from '../admins/admins-roles';
import { Building2, Download, Inbox, ShieldAlert } from 'lucide-react';
import { useCharity } from './use-charity';
import { StatementHeadlineTiles } from './statement-headline-tiles';
import { SM_MEDIA_QUERY, useMinWidth } from '../../lib/use-min-width';
import { downloadStatementWorkbook } from './statement-transport';
import {
  buildStatementView,
  donorGateClosed,
  indexStatement,
  isFiltering,
  type StatementFilters,
} from './statement-decode';
import { STATEMENT_PREFIX, StatementTable } from './statement-table';
import {
  makeStatementDateFormat,
  statementDateBoxClass,
  CHIP_OPTIONS,
  STATUS_CHIP_PAIR,
  CLEAR_SEARCH_LABEL,
  COLLAPSE_ALL_LABEL,
  DONOR_CONSENT_ACCEPTER_LINE,
  DONOR_CONSENT_LINE,
  DOWNLOAD_LABEL,
  EXPAND_ALL_LABEL,
  SEARCH_PLACEHOLDER,
  SEARCH_VISIBLE_PLACEHOLDER,
  SORT_OPTIONS,
  STATEMENT_TABS,
  TERMS_LINK_LABEL,
  TERMS_READ_LINK_LABEL,
  type StatementChip,
  type StatementSort,
  type StatementTab,
} from './statement-vocab';
import { grantExpansionKey, type StatementSearch } from './statement-search';
import { isFundraiserRowsUiEnabled } from '../../lib/env';

const PREFIX = 'grants';

export interface StatementPageProps {
  search: StatementSearch;
  onSearchChange: (next: StatementSearch) => void;
  /** Preview-only seam; production omits it and keeps the build-time env gate unchanged. */
  fundraiserPanelsEnabled?: boolean;
}

type ResolvedStatementPageProps = Omit<StatementPageProps, 'fundraiserPanelsEnabled'> & {
  fundraiserPanelsEnabled: boolean;
};

export function StatementPage({
  search,
  onSearchChange,
  fundraiserPanelsEnabled = isFundraiserRowsUiEnabled(),
}: StatementPageProps) {
  const { membership, charityId, isPending, isError, error, refetch } = useCharity();

  // ── Fail-closed loader / error / no-org gates (before any §321 read). ─────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-memberships-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={
            // `text-secondary` (7.20:1), not EmptyState's default tertiary (4.47:1): this renders on
            // the SUNKEN page ground, which is the page's worst contrast margin.
            <span className="text-text-secondary">
              {isGivmoError(error)
                ? error.message
                : 'We couldn’t load your organization. Please try again.'}
            </span>
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={refetch}
              testId={`${PREFIX}-memberships-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!membership || !charityId) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-no-org`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Claim your organization first"
          description={
            <span className="text-text-secondary">
              Once your organization is verified, your grants and donations statement appears here.
            </span>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <StatementBody
        charityId={charityId}
        // The Terms page's own gate (`terms-page.tsx`): only the org-level role accepts.
        canAcceptTerms={isOwnerRole(membership.role)}
        search={search}
        onSearchChange={onSearchChange}
        fundraiserPanelsEnabled={fundraiserPanelsEnabled}
      />
    </PageShell>
  );
}

function StatementBody({
  charityId,
  canAcceptTerms,
  search,
  onSearchChange,
  fundraiserPanelsEnabled,
}: { charityId: string; canAcceptTerms: boolean } & ResolvedStatementPageProps) {
  const dashboardQuery = useQuery(
    charityAdminGetSection321DashboardOptions({ path: { charity_id: charityId } }),
  );
  const statementQuery = useQuery(
    charityAdminGetSection321StatementOptions({ path: { charity_id: charityId } }),
  );

  // When BOTH reads fail the page has nothing to stand on, and two near-identical blocks with two
  // "Try again" buttons read as one failure reported twice (cert R1-A). One page-level alert, one
  // retry that refetches both. Any other combination keeps the per-block behaviour: one failed read
  // never blanks the other.
  if (dashboardQuery.isError && statementQuery.isError) {
    const error = statementQuery.error ?? dashboardQuery.error;
    return (
      <div role="alert">
        <EmptyState
          testId={`${PREFIX}-page-error`}
          icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
          title="We couldn’t load your donations"
          description={isGivmoError(error) ? error.message : 'Please try again in a moment.'}
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => {
                void dashboardQuery.refetch();
                void statementQuery.refetch();
              }}
              testId={`${PREFIX}-page-retry`}
            >
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <StatementHeadlineTiles
        isPending={dashboardQuery.isPending}
        isError={dashboardQuery.isError}
        error={dashboardQuery.error}
        dashboard={dashboardQuery.data ?? null}
        onRetry={() => void dashboardQuery.refetch()}
      />
      <StatementCard
        charityId={charityId}
        canAcceptTerms={canAcceptTerms}
        search={search}
        onSearchChange={onSearchChange}
        isPending={statementQuery.isPending}
        isError={statementQuery.isError}
        error={statementQuery.error}
        statement={statementQuery.data ?? null}
        onRetry={() => void statementQuery.refetch()}
        fundraiserPanelsEnabled={fundraiserPanelsEnabled}
      />
    </>
  );
}

function StatementCard({
  charityId,
  canAcceptTerms,
  search,
  onSearchChange,
  isPending,
  isError,
  error,
  statement,
  onRetry,
  fundraiserPanelsEnabled,
}: {
  charityId: string;
  canAcceptTerms: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  statement: Section321StatementOut | null;
  onRetry: () => void;
} & ResolvedStatementPageProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [draftQuery, setDraftQuery] = useState(search.q);
  // Below `sm` the toolbar's controls live in a different container than on the desk (see the
  // toolbar below); CSS cannot reparent, so the phone composition is a measured branch.
  const phoneToolbar = !useMinWidth(SM_MEDIA_QUERY);
  const stripSort = useStripSortWidth(phoneToolbar);

  // The URL is the source of truth for search text; this keeps the input in step when a link is
  // opened or the browser's back button moves the query out from under it.
  useEffect(() => setDraftQuery(search.q), [search.q]);

  const trimmedQuery = search.q.trim();
  // Searching AUTO-EXPANDS: a match three levels down is useless behind a closed chevron. Clearing the
  // collapse set on each new query (rather than ignoring it while searching) means an explicit collapse
  // during a search is still honoured.
  useEffect(() => {
    if (trimmedQuery.length > 0) setCollapsed(new Set());
  }, [trimmedQuery]);

  // `search` is re-parsed from the URL on every render, so `search.chips` is a fresh array even when
  // its value is unchanged — and the row window resets its vertical position on every new `view`.
  // Key the chips on their VALUE so `view` rebuilds only for a genuine result change (tab, sort,
  // query, chips), never for a re-render that merely dropped the consumed `grant` param (CPA-WEB36).
  const chipsKey = search.chips.join(',');
  const chips = useMemo(() => {
    const selected = new Set(chipsKey.split(','));
    return CHIP_OPTIONS.map((option) => option.value).filter((chip) => selected.has(chip));
  }, [chipsKey]);
  const filters: StatementFilters = useMemo(
    () => ({ tab: search.tab, sort: search.sort, query: search.q, chips }),
    [search.tab, search.sort, search.q, chips],
  );

  const index = useMemo(() => (statement ? indexStatement(statement) : null), [statement]);
  const donorGateIsClosed = statement !== null && donorGateClosed(statement);
  const view = useMemo(
    () => (statement && index ? buildStatementView(statement, filters, index) : null),
    [statement, index, filters],
  );
  const formatDate = useMemo(
    () => makeStatementDateFormat(statement?.covered_period),
    [statement?.covered_period],
  );
  // The date BOX travels with the date FORMAT — one covered period decides both (CPA-WEB61).
  const dateBoxClass = useMemo(
    () => statementDateBoxClass(statement?.covered_period),
    [statement?.covered_period],
  );

  const expandedKeys = useMemo(() => {
    if (!view) return new Set<string>();
    return new Set(view.expandableKeys.filter((key) => !collapsed.has(key)));
  }, [view, collapsed]);

  // The `?grant=` deep link (the `/grants/$grantId` redirect target): open that grant and bring it into
  // view. Expansion is default-on, so the only work is undoing a collapse and scrolling.
  //
  // ★ IT IS CONSUMED EXACTLY ONCE, then dropped from the URL. `view` is a fresh object on every
  // recompute, so an effect that merely depended on it re-fired on every keystroke and yanked the
  // viewport back to the deep-linked grant mid-search. The ref makes the arrival one-shot; clearing the
  // param stops a stale target riding along in every link the charity copies afterwards. Both, not
  // either: the ref alone would leave the param in the URL, and clearing alone would still fire twice
  // on the render before the navigation lands.
  const grantParam = search.grant;
  const consumedGrant = useRef<string | null>(null);
  useEffect(() => {
    if (!grantParam || !view) return;
    if (consumedGrant.current === grantParam) return;
    consumedGrant.current = grantParam;
    const key = grantExpansionKey(grantParam);
    setCollapsed((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    document
      .getElementById(`${STATEMENT_PREFIX}-section-${key.replace(/[^a-zA-Z0-9_-]+/g, '-')}`)
      // `start`, not `center`: a grant whose section is taller than the rows viewport would centre
      // its own identity/status row out of the window, leaving only child rows in view. The page half
      // of this scroll clamps at document max under the window's cap, so the row lands at the top of
      // the rows viewport and never under the app bar.
      ?.scrollIntoView({ block: 'start' });
    onSearchChange({ ...search, grant: undefined });
  }, [grantParam, view, search, onSearchChange]);

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadStatementWorkbook(charityId);
      toast.success('Spreadsheet downloaded');
    } catch (caught) {
      setDownloadFailed(true);
      toast.error(
        isGivmoError(caught)
          ? caught.message
          : 'Could not download the spreadsheet. Please try again.',
      );
    } finally {
      setDownloading(false);
    }
  };

  // Day one — a claimed org with no donations at all. One calm empty state and NO toolbar chrome: a
  // search box, four views and a sort control over an empty set are furniture for work that does not
  // exist yet. The download goes too; there is nothing to put in a file.
  const isDayOne =
    statement !== null && statement.totals.donation_count === 0 && statement.grants.length === 0;

  // The download is offered only when there is something to download. A failed read must not sit beside
  // "We couldn't load your donations" still offering a spreadsheet of them, and a pending one has
  // nothing to name yet.
  const canDownload = !isPending && !isError && statement !== null && !isDayOne;

  const allCollapsed = view !== null && view.expandableKeys.length > 0 && expandedKeys.size === 0;

  // The single sentence the live region carries. Deliberately the same FACT the visible chrome
  // states, never an extra one — the announcement is a second channel, not a second story.
  // The card joins the page's flex chain only while the table renders, so the row window can grow
  // into space that would otherwise be blank under it on a tall viewport (cert R2-B-3); the loading,
  // error and day-one states keep a content-height card.
  const showsTable = !isPending && !isError && !!statement && !!view && !!index && !isDayOne;
  const announcement = isPending
    ? 'Loading your donations.'
    : isError || statement === null || view === null
      ? // Silent on purpose: the `role="alert"` wrapper below already carries this fact, assertively.
        // Announcing it here too makes a screen reader say the failure twice, which is the exact
        // "second story" this region is written not to be.
        ''
      : isDayOne
        ? 'No donations yet.'
        : isFiltering(filters)
          ? `${view.shownCount} ${view.shownCount === 1 ? 'donation' : 'donations'} match your filters.`
          : `Showing ${view.shownCount} ${view.shownCount === 1 ? 'donation' : 'donations'}.`;

  return (
    <Card
      className={showsTable ? 'flex min-h-0 flex-1 flex-col' : undefined}
      testId={`${PREFIX}-statement-card`}
    >
      {/* No card header. The shell's top bar already says "Donations", so a title row inside the
          card said it twice — Jonathan, 2026-09-04 walkthrough: "there is no need for this Donations
          row … get rid of this top line and just move the download spreadsheet button into the far
          right of the line where you select All/by grant/etc." The views strip leads the card and
          carries the download control at its far right (`StatementTabs`). The page's heading root
          stays the hidden h1 in `PageShell`; the tablist is named "Group donations by". */}

      {/* F4 — the four states are ANNOUNCED, not only shown. One polite region, mounted for the
          card's whole life (a `role="status"` inserted together with its text is announced
          unreliably), carrying whatever the region currently is: loading, the failure, the day-one
          zero, or the live result count — which doubles as the filter-change announcement, so
          searching and chipping are no longer silent. Visually hidden: every one of these facts is
          already on screen for a sighted reader. */}
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        {...testIdProp(`${PREFIX}-statement-announcer`)}
      >
        {announcement}
      </div>

      {isPending ? (
        <div className="p-20" {...testIdProp(`${PREFIX}-statement-loading`)}>
          <Skeleton className="h-12 w-1/2" />
          <Skeleton className="mt-12 h-12 w-full" />
          <Skeleton className="mt-12 h-12 w-3/4" />
        </div>
      ) : isError || !statement || !view || !index ? (
        // `role="alert"` — assertive, because a failed read is the one state where the user is
        // waiting on an answer that is not coming. The wrapper carries it rather than EmptyState so
        // the live-system component stays untouched.
        <div role="alert">
          <EmptyState
            testId={`${PREFIX}-statement-error`}
            icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
            title="We couldn’t load your donations"
            description={isGivmoError(error) ? error.message : 'Please try again in a moment.'}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={onRetry}
                testId={`${PREFIX}-statement-retry`}
              >
                Try again
              </Button>
            }
          />
        </div>
      ) : isDayOne ? (
        <EmptyState
          testId={`${PREFIX}-statement-empty`}
          icon={<Icon icon={Inbox} size={20} aria-hidden />}
          title="Donations will appear here as they come in"
        />
      ) : (
        <StatementTabs
          active={search.tab}
          onSelect={(tab) => onSearchChange({ ...search, tab, grant: undefined })}
          trailing={
            canDownload ? (
              // Below `md` the strip is a phone's width, so the SAME control is icon-only: the label
              // stays for screen readers (`sr-only`), the title carries the tooltip, and the button
              // squares to 32px around its glyph. From `md` up the full label returns.
              // ⚠ THE 44px PHONE FLOOR around that 32px square: an invisible pseudo-element extends
              // the hit area 6px past every visible edge (32 + 12 = 44) — inside the 8px lane gap,
              // the 8px gutter and the 8px air, so it touches nothing — while the painted box stays
              // 32px. `-inset-7`, not 6: an absolute pseudo-element is placed from the PADDING box,
              // which sits 1px inside the secondary tone's border, so 7 past the padding box is 6
              // past the edge a thumb sees (measured: `-inset-6` gave 42px and a 6px-out tap missed).
              // The same device as `module-grid.tsx`'s `HIT_AREA`; Tailwind v4's `after:` variant
              // supplies the pseudo-element's content.
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                loading={downloading}
                onClick={() => void download()}
                testId={`${PREFIX}-workbook`}
                title={DOWNLOAD_LABEL}
                className="shrink-0 max-md:relative max-md:w-32 max-md:gap-0 max-md:px-0 max-md:after:absolute max-md:after:-inset-7"
              >
                <span className="max-md:sr-only">{DOWNLOAD_LABEL}</span>
              </Button>
            ) : null
          }
          notice={
            downloadFailed ? (
              // Assertive and ON SCREEN. The toast is announced politely and then vanishes; a failed
              // export needs to still be there, under the control that failed, when the user looks.
              // A line under the strip rather than a span beside the button: the strip's row is the
              // tabs' lane on a phone, and text there would squeeze them.
              <div
                role="alert"
                className="border-b border-border-subtle px-8 py-8 font-sans text-s13 tracking-givmo text-danger-700 sm:px-20"
                {...testIdProp(`${PREFIX}-workbook-error`)}
              >
                Couldn’t download the spreadsheet.
              </div>
            ) : null
          }
          toolbar={(() => {
            const searchControl = (
              <SearchInput
                value={draftQuery}
                placeholder={SEARCH_VISIBLE_PLACEHOLDER}
                aria-label={SEARCH_PLACEHOLDER}
                // The field owns the phone row, while desktop keeps the measured 320px lane. Its
                // accessible name retains the full searchable-field inventory; the visible phone
                // placeholder is intentionally shorter so it never clips.
                containerClassName="w-full sm:w-[320px]"
                onChange={(event) => {
                  setDraftQuery(event.target.value);
                  onSearchChange({ ...search, q: event.target.value });
                }}
                onClear={() => {
                  setDraftQuery('');
                  onSearchChange({ ...search, q: '' });
                }}
                testId={`${PREFIX}-search`}
              />
            );
            const sortControl = (
              <Fragment>
                <span className="font-sans text-s13 font-w500 tracking-normal text-text-secondary">
                  Sort
                </span>
                <Select
                  appearance="saas"
                  // The visible "Sort" beside this control is a sibling span, so it names nothing
                  // programmatically — without this the trigger's accessible name computes from its
                  // VALUE ("Newest"), and a screen-reader user browsing controls hears a value with
                  // no purpose. `label` would render stacked above and break the toolbar row.
                  aria-label="Sort donations"
                  options={SORT_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={search.sort}
                  onValueChange={(value) =>
                    onSearchChange({
                      ...search,
                      sort: (value ?? 'newest') as StatementSort,
                    })
                  }
                  // From `sm` the trigger is 180px: the widest label, "Amount (high→low)", needs 176
                  // (123px of text at 14px Lato plus the trigger's chrome — measured, the same figure
                  // the phone strip derives at runtime in `sortLabelNeed`) and 170 ellipsized it by
                  // 3px (review F7). The phone keeps the measured width in `--stmt-sort-w`.
                  className={
                    phoneToolbar
                      ? 'w-[var(--stmt-sort-w,170px)] flex-none'
                      : 'min-w-0 flex-1 sm:w-[180px] sm:flex-none'
                  }
                  testId={`${PREFIX}-sort`}
                />
              </Fragment>
            );
            const chipControls = CHIP_OPTIONS.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                selected={search.chips.includes(option.value)}
                onToggle={() =>
                  onSearchChange({ ...search, chips: toggleChip(search.chips, option.value) })
                }
                testId={`${PREFIX}-chip-${option.value}`}
              />
            ));
            const collapseControl = (
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                className={phoneToolbar ? 'shrink-0' : 'self-start'}
                onClick={() =>
                  setCollapsed(allCollapsed ? new Set() : new Set(view.expandableKeys))
                }
                testId={`${PREFIX}-collapse-all`}
              >
                {allCollapsed ? EXPAND_ALL_LABEL : COLLAPSE_ALL_LABEL}
              </Button>
            );

            if (phoneToolbar) {
              // The phone composition is two rows: the search field, then ONE horizontally
              // scrollable, non-wrapping strip holding Sort, the chips and Collapse-all in that
              // order (cert R1-A-2). Five stacked rows cost 193px of controls between the tabs and
              // the column header; the strip costs one. It bleeds to the card's edges (`-mx-12`)
              // so a partially visible control is the scroll cue, with no scrollbar chrome.
              return (
                <div
                  className="flex flex-col items-stretch gap-12 border-b border-border-subtle px-12 py-12"
                  {...testIdProp(`${PREFIX}-toolbar`)}
                >
                  {searchControl}
                  <div
                    ref={stripSort.stripRef}
                    className="-mx-12 flex flex-nowrap items-center gap-8 overflow-x-auto overscroll-x-contain px-12 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    {...testIdProp(`${PREFIX}-toolbar-strip`)}
                  >
                    <div
                      ref={stripSort.slotRef}
                      className="flex shrink-0 items-center gap-8"
                      style={{ '--stmt-sort-w': `${stripSort.width}px` } as CSSProperties}
                      {...testIdProp(`${PREFIX}-sort-slot`)}
                    >
                      {sortControl}
                    </div>
                    <div className="flex shrink-0 items-center gap-6 [&>button]:whitespace-nowrap">
                      {chipControls}
                    </div>
                    {collapseControl}
                  </div>
                </div>
              );
            }

            // From `sm` up: the desktop composition. `sm:flex-nowrap` remains load-bearing at the
            // narrowest sidebar desktop, where Collapse-all otherwise drops onto its own line.
            return (
              <div
                className="flex flex-col items-stretch gap-12 border-b border-border-subtle px-12 py-12 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between sm:px-20"
                {...testIdProp(`${PREFIX}-toolbar`)}
              >
                <div className="flex w-full flex-col items-stretch gap-12 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                  {searchControl}
                  <div className="flex w-full items-center gap-8 sm:w-auto">{sortControl}</div>
                  <div className="flex flex-wrap items-center gap-6 [&>button]:whitespace-nowrap sm:gap-8">
                    {chipControls}
                  </div>
                </div>
                {collapseControl}
              </div>
            );
          })()}
        >
          {trimmedQuery.length > 0 && (
            <NoticeLine testId={`${PREFIX}-search-result`}>
              <span>
                <span className="font-w600 text-text-strong">
                  {view.shownCount} {view.shownCount === 1 ? 'donation' : 'donations'}
                </span>{' '}
                {view.shownCount === 1 ? 'matches' : 'match'} “{trimmedQuery}”
              </span>
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                onClick={() => onSearchChange({ ...search, q: '' })}
                testId={`${PREFIX}-clear-search`}
              >
                {CLEAR_SEARCH_LABEL}
              </Button>
            </NoticeLine>
          )}

          {statement.truncated && (
            <NoticeLine testId={`${PREFIX}-truncated`}>
              {/* The bound is on the donation ARRAY alone; every aggregate above and in the workbook is
                  whole-history, which is exactly what this line has to say so a charity does not read
                  a windowed table as a smaller total. */}
              <span>
                Showing your most recent {statement.truncated.shown.toLocaleString()} donations.
                Totals and the spreadsheet include everything.
              </span>
            </NoticeLine>
          )}

          {search.tab === 'donor' && donorGateIsClosed && (
            // Plan 18 §3.3 (CPA-BE84): while the organization has not accepted the Charity Terms,
            // every donation sits in "Details not shared", and By donor says why, with the way to
            // the Terms. Every admin who can read this page can open the Terms page. One who cannot
            // accept is told who can, and is offered the document to read.
            <NoticeLine testId={`${PREFIX}-donor-consent`}>
              <span>
                {DONOR_CONSENT_LINE}
                {!canAcceptTerms && ` ${DONOR_CONSENT_ACCEPTER_LINE}`}{' '}
                {/* Straight after the sentence, so the two read as one thought, and wrapping with
                    it on a phone. Focus shows the house ring. `ml-8` keeps the ring's 4px clear of
                    the sentence's closing period, which an inline ring would otherwise cover. */}
                <Link
                  to="/terms"
                  className={cn(
                    'ml-8 whitespace-nowrap rounded-sm font-w600 text-brand-600 underline-offset-2 outline-none hover:text-brand-700 hover:underline',
                    FOCUS_RING,
                  )}
                  {...testIdProp(`${PREFIX}-donor-consent-terms`)}
                >
                  {canAcceptTerms ? TERMS_LINK_LABEL : TERMS_READ_LINK_LABEL}
                </Link>
              </span>
            </NoticeLine>
          )}

          <StatementTable
            tab={search.tab}
            view={view}
            index={index}
            charityId={charityId}
            formatDate={formatDate}
            dateBoxClass={dateBoxClass}
            expandedKeys={expandedKeys}
            onToggle={(key) =>
              setCollapsed((current) => {
                const next = new Set(current);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
            fundraiserPanelsEnabled={fundraiserPanelsEnabled}
            emptyState={
              <EmptyState
                testId={`${PREFIX}-statement-no-matches`}
                icon={<Icon icon={Inbox} size={20} aria-hidden />}
                title="No donations match your filters"
                description={
                  isFiltering(filters)
                    ? 'Try a different search, or clear the filters to see everything.'
                    : undefined
                }
              />
            }
          />
        </StatementTabs>
      )}
    </Card>
  );
}

/**
 * Chips add and remove in the canonical option order, except that choosing one of the status pair
 * clears the other: `Sent` and `Not yet sent` partition one field, so both at once could only ever
 * match nothing — a false empty state on a money page (cert R2-B-1). `Has designation` is
 * independent of either.
 */
function toggleChip(chips: readonly StatementChip[], chip: StatementChip): StatementChip[] {
  if (chips.includes(chip)) return chips.filter((entry) => entry !== chip);
  const displaced = displacedBy(chip);
  return CHIP_OPTIONS.filter(
    (option) =>
      option.value === chip || (chips.includes(option.value) && !displaced.includes(option.value)),
  ).map((option) => option.value);
}

/** The status chip that `chip` displaces, when `chip` is one of the pair. */
function displacedBy(chip: StatementChip): readonly StatementChip[] {
  return STATUS_CHIP_PAIR.includes(chip) ? STATUS_CHIP_PAIR.filter((entry) => entry !== chip) : [];
}

/**
 * The phone strip's Sort select: its nominal width, the narrowest it may ever go, the cue it buys
 * (how much of the first cut control must show, and how much of it must be past the edge for the
 * cut to be unmistakable), and how far past the nominal it may grow to buy that cue.
 */
export const STRIP_SORT = { base: 170, floor: 130, peek: 24, cutMin: 12, grow: 64 } as const;

/**
 * The width the phone strip's Sort select takes. Two constraints (cert R2-B-1b, review F7):
 *
 *   · No option label is ever ellipsized: the select is never narrower than `labelNeed`, the width
 *     its widest label needs inside the trigger's chrome — measured at runtime, so the copy and the
 *     font, not a device number, decide it.
 *   · The strip's scroll cue is a control the lane's edge cuts UNMISTAKABLY: at least `peek` of the
 *     first control past the edge shows and at least `cutMin` of it is past the edge. A control that
 *     starts 1px beyond the edge (390 before the cue) or ends 1px past it says nothing.
 *
 * Searching outward from the nominal width — shrinking first, never below the label need or the
 * floor, growing otherwise up to `grow` past the nominal — and settling for the nominal if nothing
 * satisfies both. `controls` are the boxes AFTER the select with the select at ZERO width, in the
 * lane's coordinates at rest; a candidate width shifts them all right by that width.
 */
export function stripSortWidth(
  laneRight: number,
  controls: readonly { left: number; right: number }[],
  { base, floor, peek, cutMin, grow }: typeof STRIP_SORT = STRIP_SORT,
  labelNeed = 0,
): number {
  const min = Math.max(floor, labelNeed);
  const nominal = Math.max(base, min);
  const ceiling = nominal + grow;
  const cue = (width: number) => {
    const cut = controls.find((control) => control.right + width > laneRight + 0.5);
    if (!cut) return true;
    const visible = laneRight - (cut.left + width);
    const past = cut.right + width - laneRight;
    return visible >= peek && past >= cutMin;
  };
  if (cue(nominal)) return nominal;
  for (let delta = 1; delta <= Math.max(nominal - min, ceiling - nominal); delta += 1) {
    if (nominal - delta >= min && cue(nominal - delta)) return nominal - delta;
    if (nominal + delta <= ceiling && cue(nominal + delta)) return nominal + delta;
  }
  return nominal;
}

/**
 * The width the widest sort label needs inside the select's trigger: the label's text width in the
 * value's own font plus the trigger's chrome. The chrome is measured INDEPENDENTLY of the current
 * label — the space before the value box, the trigger's gap, and everything from the chevron to the
 * trigger's right edge — because the value box is content-sized under a short label, and "trigger
 * minus value" would then grow with every re-measure. 0 where text cannot be measured (jsdom has no
 * canvas), so the pure function's nominal stands there.
 */
function sortLabelNeed(trigger: HTMLElement): number {
  const value = trigger.firstElementChild;
  const icon = trigger.lastElementChild;
  const context = document.createElement('canvas').getContext?.('2d');
  if (!value || !icon || icon === value || !context) return 0;
  const style = getComputedStyle(value);
  context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const widest = Math.max(...SORT_OPTIONS.map((option) => context.measureText(option.label).width));
  if (!Number.isFinite(widest) || widest <= 0) return 0;
  const box = trigger.getBoundingClientRect();
  const valueBox = value.getBoundingClientRect();
  const iconBox = icon.getBoundingClientRect();
  const gap = parseFloat(getComputedStyle(trigger).columnGap) || 8;
  const chrome = valueBox.left - box.left + gap + (box.right - iconBox.left);
  return Math.ceil(widest + chrome) + 2;
}

/**
 * `stripSortWidth` against the live strip: measured on mount, when fonts land (label widths change)
 * and on every resize of the strip. The boxes are read back into zero-width terms — shifted left by
 * the select's current width — so the result converges in one step and never oscillates. Inactive
 * (the desk composition) it measures nothing.
 */
function useStripSortWidth(active: boolean) {
  // Callback refs, not `useRef`: the strip mounts after the statement loads, so the effect has to
  // re-run when the nodes attach, not only when `active` flips.
  const [strip, setStrip] = useState<HTMLDivElement | null>(null);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(STRIP_SORT.base);
  useLayoutEffect(() => {
    if (!active || !strip || !slot) return undefined;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const trigger = slot.querySelector('button');
      const current = trigger ? trigger.getBoundingClientRect().width : STRIP_SORT.base;
      const shift = strip.scrollLeft - current;
      const controls = [...strip.querySelectorAll('button')]
        .filter((button) => !slot.contains(button))
        .map((button) => {
          const box = button.getBoundingClientRect();
          return { left: box.left + shift, right: box.right + shift };
        });
      const need = trigger ? sortLabelNeed(trigger) : 0;
      setWidth(stripSortWidth(strip.getBoundingClientRect().right, controls, STRIP_SORT, need));
    };
    measure();
    if ('fonts' in document) void document.fonts.ready.then(measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(strip);
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [active, strip, slot]);
  return { stripRef: setStrip, slotRef: setSlot, width };
}

/**
 * The four views, on the design system's `Tabs` (Base UI).
 *
 * This was a hand-rolled `role="tablist"` strip, justified by "the selection lives in the URL, so it
 * has to survive a reload — a different contract from Tabs' internal panel state." That was wrong:
 * `Tabs.Root` takes a CONTROLLED `value`/`onValueChange`, so URL-driven selection works against the
 * primitive, and the fork was giving up what it ships — roving tabindex, arrow-key navigation, and
 * real tab↔panel association.
 *
 * The four `Tabs.Panel`s look like duplication and are not: Base UI unmounts an inactive panel, so
 * exactly one renders, and each tab gets the `aria-controls`/`aria-labelledby` pair it needs. The
 * className overrides carry the card's own chrome — the strip is flush inside the card's 20px
 * gutter, its rule is `border-subtle`, and the panel has no top padding because the toolbar follows
 * immediately. The house focus outline is passed through `Tab`'s className: the primitive ships
 * ring-only focus (1.23:1), which is design-system debt, but this surface does not have to inherit it.
 */
function StatementTabs({
  active,
  onSelect,
  trailing,
  notice,
  toolbar,
  children,
}: {
  active: StatementTab;
  onSelect: (tab: StatementTab) => void;
  /**
   * The far-right end of the views strip — the whole-history download. It shares the strip's ONE
   * row with the tabs (Jonathan, 2026-09-04: no card title row; the button "into the far right of
   * the line where you select All/by grant/etc."), OUTSIDE the tablist so the tablist owns only tabs.
   */
  trailing?: React.ReactNode;
  /** A line under the strip — the download failure, kept on screen as an alert. */
  notice?: React.ReactNode;
  /**
   * Rendered between the strip and the panel, OUTSIDE the panel on purpose. Search, sort, chips and
   * collapse-all are shared by all four views — they are not the selected tab's content — and while
   * they lived inside the panel a tab change destroyed and recreated them, taking focus out of the
   * search box with it. State already lived in the parent and survived; the DOM did not.
   */
  toolbar: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Tabs.Root
      appearance="saas"
      value={active}
      onValueChange={(next) => onSelect(next as StatementTab)}
      className="min-h-0 flex-1"
    >
      {/* ONE row for the four views and the download. The tablist is the flexible lane (`min-w-0
          flex-1`, scrollable sideways) and the control keeps its own width (`shrink-0`), so the
          control can never overlap or clip the tabs. The lane fits at 390, the ruling's phone width,
          and pans below 376px: at 320 "By donor" is cut at the lane's edge, the same affordance the
          toolbar's chip lane uses there, while the page itself never scrolls sideways. The rule under
          the row is the row's, not the tablist's, so it runs under the control too. `h-48` is the
          ruling's "tad taller" as a token: the strip was 40px, the scale's next steps are 44 and 48,
          and 48 gives the 32px control an even 8px of air above and below — the same 8px as its
          phone gutter. */}
      <div
        className="flex items-center gap-8 border-b border-border-subtle pr-8 sm:gap-16 sm:pr-20"
        {...testIdProp(`${PREFIX}-tabs-row`)}
      >
        <Tabs.List
          aria-label="Group donations by"
          className="min-w-0 flex-1 gap-8 overflow-x-auto border-b-0 px-8 sm:gap-24 sm:px-20"
        >
          {STATEMENT_TABS.map((tab) => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              className="h-48 shrink-0 whitespace-nowrap py-0 focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
              testId={`${PREFIX}-tab-${tab.key}`}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {trailing}
      </div>
      {notice}
      {toolbar}
      {STATEMENT_TABS.map((tab) => (
        <Tabs.Panel
          key={tab.key}
          value={tab.key}
          className="flex min-h-0 flex-1 flex-col pt-0"
          // The panel always holds the toolbar and the row window, which are the real stops.
          focusable={false}
        >
          {tab.key === active ? children : null}
        </Tabs.Panel>
      ))}
    </Tabs.Root>
  );
}

function NoticeLine({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div
      className="flex items-center justify-between gap-12 border-b border-border-subtle px-20 py-10 font-sans text-s13 tracking-givmo text-text-secondary"
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  // No visible PageHeader: the console's top bar already names this screen ("Donations", the
  // module's name since the A7 rename), and the design leads with the statutory tiles rather than
  // restating the title above them. But dropping PageHeader dropped the page's `<h1>` with it,
  // leaving heading navigation nothing to find on the portal's primary money screen — so the heading
  // is kept and hidden, and it says what the top bar says: with the card's title row gone (W5) it is
  // the only heading naming the screen. The visible composition is byte-identical; only the
  // accessibility tree gains a root.
  //
  // 1184px, not the 1120 the narrower charity pages use: that is exactly the shell's content column
  // at the 1440 width the design was drawn at, so the card lands at the ratified 1136 and fills the
  // column flush instead of sitting 32px inset. This is the app's widest table — six columns of money
  // plus a designation — and the 64px the cap was costing is what stops the designation wrapping. The
  // cap itself is kept (the app sizes every page to its content: 560 / 900 / 960 / 1000 / 1120) so an
  // ultrawide monitor does not stretch the money columns apart.
  return (
    <div
      className="mx-auto flex min-h-[calc(100dvh-var(--charity-app-bar-h,0px))] w-full max-w-[1184px] flex-col gap-24 px-24 py-24 md:px-32 lg:px-24"
      {...testIdProp('page-grants')}
    >
      <h1 className="sr-only">Donations</h1>
      {children}
    </div>
  );
}
